import { useCallback, useMemo, useState } from "react";

/** Multi-select helper for list views. Tracks a Set of ids + helpers. */
export function useSelection() {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);

  const setAll = useCallback((ids: string[]) => setSelected(new Set(ids)), []);

  const isSelected = useCallback((id: string) => selected.has(id), [selected]);

  const ids = useMemo(() => [...selected], [selected]);

  const allSelected = useCallback(
    (visibleIds: string[]) => visibleIds.length > 0 && visibleIds.every((id) => selected.has(id)),
    [selected],
  );

  const toggleAll = useCallback((visibleIds: string[]) => {
    setSelected((prev) => {
      const allSelectedNow = visibleIds.every((id) => prev.has(id));
      if (allSelectedNow) {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      visibleIds.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  return { selected, ids, count: selected.size, toggle, toggleAll, clear, setAll, isSelected, allSelected };
}
